// Device / viewport detection. Drives the responsive layout split between the
// desktop chrome (top pill nav, floating profile dock) and the mobile chrome
// (iOS-style bottom tab bar, top action row).
//
// We key the layout off a CSS media query (reliable, reflows on rotation), and
// expose iOS / standalone flags for safe-area and PWA niceties.

import { useEffect, useState } from 'react';

/** Reactive CSS media-query hook. SSR-safe (returns false until mounted). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    // Safari < 14 uses addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSClassic = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; disambiguate via touch points.
  const iPadOS = ua.includes('Macintosh') && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return iOSClassic || iPadOS;
}

export interface DeviceInfo {
  isMobile: boolean;      // narrow viewport — drives the layout
  isTouch: boolean;       // coarse pointer
  isIOS: boolean;         // iPhone / iPad / iPod
  isStandalone: boolean;  // launched from the home screen (installed PWA)
}

/**
 * Single source of truth for the responsive layout.
 *   • isMobile is viewport-driven (max-width: 767px) so it reflows on resize
 *     and rotation — not just a one-shot user-agent sniff.
 *   • isIOS / isStandalone are device facts used for safe-area + PWA polish.
 */
export function useDevice(): DeviceInfo {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTouch = useMediaQuery('(pointer: coarse)');
  const [iosFlags, setIosFlags] = useState({ isIOS: false, isStandalone: false });

  useEffect(() => {
    const standalone =
      (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
      // iOS Safari exposes navigator.standalone.
      (typeof navigator !== 'undefined' && (navigator as unknown as { standalone?: boolean }).standalone === true);
    setIosFlags({ isIOS: detectIOS(), isStandalone: Boolean(standalone) });
  }, []);

  return { isMobile, isTouch, ...iosFlags };
}
